import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setActiveSearch,
  clearActiveSearch,
  clearSLRContext,
} from "../../../../store/literatureDiscoverySlice";
import GetCookie from "../../../utils/GetCookie";
import authNavigate from "../../../globalComponents/AuthNav";
import ProcessingPopup from "../../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../../utils/popup/ErrorPopup";
import SuccessPopup from "../../../utils/popup/SuccessPopup";

// ═══════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════

const API_BASE_URL = "/pceapi/slr";
const AUTO_REFRESH_MS = 30000;
const RUNNING_STATES = new Set(["RUNNING", "IN_PROGRESS", "PROCESSING", "QUEUED", "STARTED"]);

const num = (v) => Number(v) || 0;
const fmt = (v) => num(v).toLocaleString();
const pctOf = (done, total) => (total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0);
const upper = (v) => String(v || "").toUpperCase();
const isRunning = (s) => RUNNING_STATES.has(upper(s));

// "SINGLE_AI_ONLY" → "Single AI only". Known acronyms stay uppercase.
const ACRONYMS = new Set(["AI", "PICO", "PICOS", "PICOT", "PECO", "SPIDER", "SPICE", "QC", "FT"]);
const formatEnum = (value) => {
  if (!value) return "";
  const words = String(value).trim().split(/[_\s]+/).filter(Boolean);
  return words
    .map((w, i) => {
      const up = w.toUpperCase();
      if (ACRONYMS.has(up)) return up;
      const low = w.toLowerCase();
      return i === 0 ? low.charAt(0).toUpperCase() + low.slice(1) : low;
    })
    .join(" ");
};

// Name shown in the Textractor templates list.
// - Sheet created and renamed by the user -> that name.
// - Otherwise -> "display_name" from the template JSON (sent by the list API).
// Sheets created before display_name existed are named after the template file
// (study_detail, study_detail_slr_522, ...); those also show the JSON name.
const formatTemplateName = (name = "") => {
  const text = String(name).replace(/_/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const textractorTemplateLabel = (t) => {
  const key = String(t?.template_name || "").toLowerCase();
  const sheetName = String(t?.table_name || "").trim();
  const isOldTemplateName = sheetName.toLowerCase().replace(/[\s-]+/g, "_").startsWith(key);
  if (sheetName && !isOldTemplateName) return sheetName;
  return t?.display_name || formatTemplateName(t?.template_name);
};

// Normalises one phase block from /fabric-details into a flat shape.
const buildPhase = (p) => {
  const total = num(p?.total);
  const ai = p?.ai_screening || {};
  const human = p?.human_screening || {};
  const cr = p?.conflict_resolution || {};
  const qc = p?.qc || {};
  const st = p?.statuses || {};
  const link = p?.link_full_text;

  const humanTotal = num(human.total);
  const humanReviewed = num(human.reviewed);
  const crTotal = num(cr.total);
  const crResolved = num(cr.resolved);

  return {
    total,
    hasAi: num(p?.llm_count) > 0 || num(ai.total) > 0,
    ai: {
      total: num(ai.total) || total,
      reviewed: num(ai.reviewed),
      conflicts: num(ai.conflicts),
      running: isRunning(st.ai_status),
    },
    human: {
      total: humanTotal,
      reviewed: humanReviewed,
      pending: Math.max(humanTotal - humanReviewed, 0),
    },
    conflicts: {
      total: crTotal,
      resolved: crResolved,
      unresolved: Math.max(crTotal - crResolved, 0),
      applicable: upper(st.conflict_resolution_status) !== "INAPPLICABLE",
    },
    qc: {
      total: num(qc.total),
      included: num(qc.included),
      excluded: num(qc.excluded),
      done: upper(st.qc_status) === "COMPLETED",
    },
    link: link
      ? {
          total: num(link.total),
          linked: num(link.linked),
          pending: num(link.pending),
          running: isRunning(st.linking_status),
        }
      : null,
  };
};

// Continuous phase progress: 100 only when QC is completed, otherwise the
// mean of every track that has work in it (capped at 99).
const phasePct = (ph) => {
  if (ph.qc.done) return 100;
  if (ph.total === 0) return 0;
  const parts = [];
  if (ph.link && ph.link.total > 0) parts.push(ph.link.linked / ph.link.total);
  if (ph.hasAi) parts.push(ph.ai.total > 0 ? ph.ai.reviewed / ph.ai.total : 0);
  if (ph.human.total > 0) parts.push(ph.human.reviewed / ph.human.total);
  if (ph.conflicts.total > 0) parts.push(ph.conflicts.resolved / ph.conflicts.total);
  parts.push(ph.qc.total / ph.total);
  const mean = parts.reduce((a, b) => a + b, 0) / parts.length;
  return Math.min(99, Math.round(mean * 100));
};

const STATUS_META = {
  COMPLETED: { label: "Completed", pill: "discovery-badge-green" },
  IN_PROGRESS: { label: "In progress", pill: "discovery-badge-blue" },
  READY: { label: "Ready to start", pill: "discovery-badge-blue" },
  NOT_STARTED: { label: "Not started", pill: "discovery-badge-blue" },
  LOCKED: { label: "Locked", pill: "discovery-badge-orange" },
  COMING_SOON: { label: "Coming soon", pill: "discovery-badge-yellow" },
};

// Circle colours come from the discovery-badge-* classes (Slr.css), which read
// Variables.css tokens, so they follow the light and dark themes.
const STEP_DOT_CLASS = {
  COMPLETED: "discovery-badge-green",
  IN_PROGRESS: "discovery-badge-blue",
  READY: "discovery-badge-blue",
  NOT_STARTED: "discovery-badge-blue",
  LOCKED: "discovery-badge-orange",
  COMING_SOON: "discovery-badge-yellow",
};

// Chevron fill per status → .slr-chevron--{tone} in Slr.css
const CHEVRON_TONE = {
  COMPLETED: "done",
  IN_PROGRESS: "active",
  READY: "active",
  NOT_STARTED: "active",
  LOCKED: "locked",
  COMING_SOON: "soon",
};

// Chart fills come from .slr-fill-* classes in Slr.css (fill: var(--colorN)),
// so colours follow Variables.css in both themes with nothing hardcoded here.
const FILL = {
  included: "slr-fill-included",
  excludedFt: "slr-fill-excluded-ft",
  notAssessed: "slr-fill-not-assessed",
  excludedAbs: "slr-fill-excluded-abs",
  notScreened: "slr-fill-not-screened",
};

// ═══════════════════════════════════════════════════════════════
// Small presentational pieces
// ═══════════════════════════════════════════════════════════════

const StepBadge = ({ status, num: n }) => {
  let content = n;
  if (status === "COMPLETED") content = <i className="bi bi-check-lg nx-phase-check-icon" aria-hidden="true" />;
  if (status === "LOCKED") content = <i className="bi bi-lock" aria-hidden="true" />;
  return (
    <span
      className={`nx-phase-dot ${STEP_DOT_CLASS[status] || STEP_DOT_CLASS.NOT_STARTED} rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 p-0`}
    >
      {content}
    </span>
  );
};

const ColorDot = ({ color, size = 10 }) => (
  <svg width={size} height={size} viewBox="0 0 10 10" aria-hidden="true" className="flex-shrink-0">
    <circle cx="5" cy="5" r="5" className={color} />
  </svg>
);

// Days / months since a date, for "how old is the search" checks.
const daysSince = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.max(0, Math.floor((Date.now() - t) / 86400000));
};
const ageLabel = (days) => {
  if (days === null) return "—";
  if (days < 1) return "today";
  if (days < 60) return `${days} days ago`;
  const months = Math.round(days / 30);
  return months < 24 ? `${months} months ago` : `${Math.round(months / 12)} years ago`;
};
// Cochrane guidance: rerun searches older than ~12 months before publishing.
const searchAgeTone = (days) =>
  days === null ? "discovery-badge-neutral" : days > 365 ? "discovery-badge-red" : days > 180 ? "discovery-badge-yellow" : "discovery-badge-green";
const initials = (name = "") =>
  name
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

// Single horizontal bar (0–100) in SVG, so its width needs no inline style.
const MiniBar = ({ pct, color }) => (
  <svg width="100%" height="8" viewBox="0 0 100 8" preserveAspectRatio="none" aria-hidden="true">
    <rect x="0" y="0" width="100" height="8" rx="2" className={color} opacity="0.15" />
    <rect x="0" y="0" width={Math.max(1, Math.min(100, pct))} height="8" rx="2" className={color} />
  </svg>
);

// Proportional bar drawn in SVG so segment widths need no inline styles.
const SplitBar = ({ parts }) => {
  const total = parts.reduce((a, p) => a + p.n, 0);
  if (total === 0) return null;
  const visible = parts.filter((p) => p.n > 0);
  const gap = visible.length > 1 ? 0.6 : 0;
  const usable = 100 - gap * (visible.length - 1);
  let x = 0;
  return (
    <svg width="100%" height="10" viewBox="0 0 100 10" preserveAspectRatio="none" role="img" aria-label="Decision split">
      {visible.map((p) => {
        const w = Math.max(0.8, (p.n / total) * usable);
        const rect = <rect key={p.label} x={x} y="0" width={w} height="10" rx="1.5" className={p.color} />;
        x += w + gap;
        return rect;
      })}
    </svg>
  );
};

// ═══════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════

const ScreeningSpaceProjectOverview = ({
  slrFabricDetails,
  onBackToProjects,
  onCreateProject,
  onEditProject,
  onOpenScreening,
  onOpenDataExtraction,
  onOpenGenbi,
  onOpenTextractor,
  onRefresh,
  onStatsLoaded,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const headers = useMemo(() => ({ Authorization: GetCookie("access_token") }), []);

  // ═══ Server data ═══
  const [fabricDetails, setFabricDetails] = useState(null);
  const [linkedSearches, setLinkedSearches] = useState([]);
  const [abstractsCount, setAbstractsCount] = useState(0);
  const [migratedCount, setMigratedCount] = useState(0);
  const [manualCount, setManualCount] = useState(0);
  const [genbiTemplates, setGenbiTemplates] = useState([]);
  const [textractorTemplates, setTextractorTemplates] = useState([]);
  const [extractionStats, setExtractionStats] = useState(null);
  const [prismaData, setPrismaData] = useState(null);

  const [loadingGenbi, setLoadingGenbi] = useState(false);
  const [loadingTextractor, setLoadingTextractor] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ═══ UI state ═══
  const [showPico, setShowPico] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);

  // ═══ Popups ═══
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState("");
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState("");

  const closeErrorPopup = () => setIsErrorPopupVisible(false);
  const closeSuccessPopup = () => setIsSuccessPopupVisible(false);
  const showError = (msg) => {
    setPopupErrorMessage(msg);
    setIsErrorPopupVisible(true);
  };

  // ═══ Derived IDs ═══
  const fabricId = slrFabricDetails?.slr_fabric_id;
  const configId = slrFabricDetails?.config_id;
  const reportDocsThreadId = slrFabricDetails?.stages?.general_settings?.data?.report_docs_thread_id;
  const projectName = slrFabricDetails?.stages?.general_settings?.data?.name || "SLR Project";

  const handleUnauthorized = (res) => {
    if (res?.data?.status === "NotAuthorized") {
      authNavigate("/login", "iam", navigate);
      return true;
    }
    return false;
  };

  // ═══ Fetchers ═══

  const fetchFabricDetails = useCallback(async () => {
    if (!fabricId) return;
    try {
      const res = await axios.post(`${API_BASE_URL}/fabric-details`, { slr_fabric_id: fabricId }, { headers });
      if (handleUnauthorized(res)) return;
      if (res.data?.status === "success") setFabricDetails(res.data.data);
    } catch (err) {
      console.error("Fabric details fetch failed", err);
    }
  }, [fabricId, headers, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLinkedSearches = useCallback(async () => {
    if (!fabricId) return;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/discovery/linked-searches`,
        { slr_fabric_id: fabricId },
        { headers }
      );
      if (handleUnauthorized(res)) return;
      if (res.data?.status === "success") {
        setLinkedSearches(res.data.data || []);
        setAbstractsCount(res.data.abstracts_count || 0);
        setMigratedCount(res.data.migrated_count || 0);
        setManualCount(res.data.manual_count || 0);
      }
    } catch (err) {
      console.error("Failed to fetch linked searches", err);
    }
  }, [fabricId, headers, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGenbiTemplates = useCallback(async () => {
    if (!configId) return;
    setLoadingGenbi(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/genbi-templates/list`,
        { slr_fabric_id: configId },
        { headers }
      );
      if (handleUnauthorized(res)) return;
      if (res.data?.status === "success") setGenbiTemplates(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch GenBI templates", err);
    } finally {
      setLoadingGenbi(false);
    }
  }, [configId, headers, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTextractorTemplates = useCallback(async () => {
    if (!configId) return;
    setLoadingTextractor(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/textractor-templates/list`,
        { slr_fabric_id: configId },
        { headers }
      );
      if (handleUnauthorized(res)) return;
      if (res.data?.status === "success") setTextractorTemplates(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch Textractor templates", err);
    } finally {
      setLoadingTextractor(false);
    }
  }, [configId, headers, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchExtractionStats = useCallback(async () => {
    if (!fabricId) return;
    try {
      const [checkRes, listRes] = await Promise.all([
        axios.post("/pceapi/textractor/check-fabric-models", { fabric_id: fabricId }, { headers }),
        axios.post("/pceapi/textractor/list-models", { fabric_id: fabricId, page: 1, size: 100 }, { headers }),
      ]);
      const modelCount = checkRes.data?.model_count || 0;
      const models = listRes.data?.data?.flatMap((f) => f.models || []) || [];
      setExtractionStats({
        model_count: modelCount,
        completed_count: models.filter((m) => m.status === "COMPLETED").length,
        total_count: models.length,
      });
    } catch (err) {
      console.error("Failed to fetch extraction stats", err);
    }
  }, [fabricId, headers]);

  // PRISMA counts + top exclusion reasons (existing /prisma_data endpoint).
  const fetchPrismaData = useCallback(async () => {
    if (!fabricId) return;
    try {
      const res = await axios.post(`${API_BASE_URL}/prisma_data`, { slr_fabric_id: fabricId }, { headers });
      if (handleUnauthorized(res)) return;
      if (res.data?.status === "success") setPrismaData(res.data.data);
    } catch (err) {
      console.error("Failed to fetch PRISMA data", err);
    }
  }, [fabricId, headers, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══ Initial load — one consolidated effect (see original note on double-fire).
  useEffect(() => {
    if (!fabricId) return;
    setAbstractsCount(0);
    setMigratedCount(0);
    setManualCount(0);
    setIsProcessingPopupVisible(true);
    setProcessingMessage("Loading project data...");
    Promise.all([
      fetchFabricDetails(),
      fetchLinkedSearches(),
      fetchExtractionStats(),
    ]).finally(
      () => {
        setIsProcessingPopupVisible(false);
        setProcessingMessage("");
        onStatsLoaded?.();
      }
    );
    // Insights only — loads alongside without holding the processing popup.
    fetchPrismaData();
  }, [fabricId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Template lists need config_id, which arrives after the skeleton project
  // details — load them when it is available (and again if the project changes).
  useEffect(() => {
    if (!configId) return;
    fetchGenbiTemplates();
    fetchTextractorTemplates();
  }, [configId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showPico) return;
    const onKey = (e) => e.key === "Escape" && setShowPico(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showPico]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setIsProcessingPopupVisible(true);
    setProcessingMessage("Refreshing project data...");
    try {
      await Promise.all([
        fetchFabricDetails(),
        fetchLinkedSearches(),
        fetchGenbiTemplates(),
        fetchTextractorTemplates(),
        fetchExtractionStats(),
        fetchPrismaData(),
      ]);
      onRefresh?.();
    } finally {
      setIsRefreshing(false);
      setIsProcessingPopupVisible(false);
      setProcessingMessage("");
    }
  };

  // ═══ Derived phase data ═══
  const abs = useMemo(() => buildPhase(fabricDetails?.abstract_screening), [fabricDetails]);
  const ft = useMemo(() => buildPhase(fabricDetails?.full_text_screening), [fabricDetails]);

  const anyRunning = abs.ai.running || ft.ai.running || !!ft.link?.running;

  // Silent auto-refresh while any AI or linking job is running.
  useEffect(() => {
    if (!anyRunning || !fabricId) return;
    const id = setInterval(fetchFabricDetails, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [anyRunning, fabricId, fetchFabricDetails]);

  // ═══ Navigation handlers ═══

  const handleLiteratureDiscoveryClick = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage("Opening Literature Discovery...");

    const baseState = {
      fromSLR: true,
      slrFabricId: fabricId,
      slrProjectName: projectName,
      returnPath: window.location.pathname,
    };
    const goToSearch = (search) => {
      dispatch(setActiveSearch(search));
      navigate("/literature-discovery", { state: { ...baseState, openDashboard: true, linkedSearch: search } });
    };
    const goToCreate = () => navigate("/literature-discovery", { state: { ...baseState, openCreateForm: true } });

    try {
      // Fetch fresh to avoid the race where linkedSearches hasn't loaded yet.
      const res = await axios.post(
        `${API_BASE_URL}/discovery/linked-searches`,
        { slr_fabric_id: fabricId },
        { headers }
      );
      if (handleUnauthorized(res)) return;
      const fresh = res.data?.status === "success" ? res.data.data || [] : [];
      setLinkedSearches(fresh);
      fresh.length > 0 ? goToSearch(fresh[0]) : goToCreate();
    } catch (err) {
      console.error("Failed to resolve linked search before navigating", err);
      linkedSearches.length > 0 ? goToSearch(linkedSearches[0]) : goToCreate();
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  const handleScreeningClick = () => onOpenScreening?.();
  const handleDataExtractionClick = () => onOpenDataExtraction?.();

  // Same flow as GenBI: create the project's sheet from the template (Phase 2 QC
  // INCLUDED documents only) or open the one already created, then hand off to
  // the embedded Data Extraction view.
  const executeOpenTextractorTemplate = async (template) => {
    if (!template) return;
    setIsProcessingPopupVisible(true);
    setProcessingMessage(template.is_created ? "Opening Textractor workspace..." : "Creating extraction sheet...");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/textractor-templates/create-or-open`,
        { slr_textractor_template_id: template.id, slr_fabric_id: configId },
        { headers }
      );
      if (handleUnauthorized(res)) return;
      if (res.data?.status !== "success") {
        showError(res.data?.message || "Failed to open Textractor template");
        return;
      }
      const data = res.data.data || {};
      const tableId = data.table_id || res.data.table_id;
      const textractorFabricId = data.fabric_id || res.data.fabric_id;
      if (!tableId || !textractorFabricId) {
        console.error("Missing IDs from API response", res.data);
        showError("Missing data in response");
        return;
      }
      onOpenTextractor?.({
        fromSLR: true,
        tableId,
        tableName: data.table_name,
        fabricId: textractorFabricId,
        fabricName: data.fabric_name || projectName,
        action: res.data.action,
        slrTemplateName: template.template_name,
      });
    } catch (err) {
      console.error("Failed to open Textractor template", err);
      showError("Failed to open Textractor template");
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  const executeOpenTemplate = async (template) => {
    if (!template) return;
    setIsProcessingPopupVisible(true);
    setProcessingMessage("Opening Gen BI workspace...");
    try {
      const res = await axios.post(
        `${API_BASE_URL}/genbi-templates/create-or-open`,
        { slr_genbi_template_id: template.id, slr_fabric_id: configId },
        { headers }
      );
      if (handleUnauthorized(res)) return;
      if (res.data?.status !== "success") {
        showError(res.data?.message || "Failed to open Gen BI template");
        return;
      }
      const action = res.data.action;
      const templateId = res.data.data?.id || res.data.template_id;
      const genBiFabricId = res.data.data?.fabric_id || res.data.fabric_id;
      if (!templateId || !genBiFabricId) {
        console.error("Missing IDs from API response", res.data);
        showError("Missing data in response");
        return;
      }
      onOpenGenbi?.({
        fromSLR: true,
        slrFabricId: genBiFabricId,
        slrTemplateName: template.template_name,
        templateId,
        action,
        templateStructure: action === "created" ? res.data.data : null,
        fabricIdForTextractor: fabricId,
        reportDocsThreadId: reportDocsThreadId || null,
      });
    } catch (err) {
      console.error("Failed to open template", err);
      showError("Failed to open Gen BI template");
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // ═══ Excel export via /download-screening (StreamingResponse) ═══
  const handleDownload = async (mode) => {
    if (!mode || !fabricId) return;
    setIsDownloading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/download-screening`,
        { slr_fabric_id: fabricId, mode },
        { headers, responseType: "blob" }
      );

      // Errors come back as JSON even though we asked for a blob.
      if (res.data?.type?.includes("application/json")) {
        const body = JSON.parse(await res.data.text());
        if (body?.status === "NotAuthorized") {
          authNavigate("/login", "iam", navigate);
          return;
        }
        showError(body?.message || "Couldn't download the screening export.");
        return;
      }

      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] || `${projectName}_${mode}.xlsx`;

      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Screening download failed", err);
      showError("Couldn't download the screening export.");
    } finally {
      setIsDownloading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // Step model
  // ═══════════════════════════════════════════════════════════════

  const discoveryStatus = (() => {
    const hasSearch = linkedSearches.length > 0;
    const hasArticles = abstractsCount > 0;
    // A migration still moving abstracts in keeps the step open — the count
    // is still growing, so "Completed" would be premature.
    const migrating = linkedSearches.some((s) => {
      const st = String(s.abstract_migration_status || "").toUpperCase();
      return RUNNING_STATES.has(st) || st === "PENDING";
    });
    if (migrating) return "IN_PROGRESS";
    if (hasSearch && hasArticles) return "COMPLETED";
    if (hasSearch || hasArticles) return "IN_PROGRESS";
    return "NOT_STARTED";
  })();

  const absStatus = (() => {
    if (abs.total === 0) return discoveryStatus === "NOT_STARTED" ? "LOCKED" : "NOT_STARTED";
    if (abs.qc.done) return "COMPLETED";
    return "IN_PROGRESS";
  })();

  const ftStatus = (() => {
    if (ft.total === 0) return "LOCKED";
    if (ft.qc.done) return "COMPLETED";
    const started = (ft.link?.linked || 0) > 0 || ft.ai.reviewed > 0 || ft.human.reviewed > 0;
    return started ? "IN_PROGRESS" : "NOT_STARTED";
  })();

  const extractionStatus = (() => {
    if (ft.qc.included === 0) return "LOCKED";
    const modelCount = extractionStats?.model_count || 0;
    const completed = extractionStats?.completed_count || 0;
    const total = extractionStats?.total_count || 0;
    if (modelCount > 0 && completed > 0 && completed === total) return "COMPLETED";
    if (modelCount > 0) return "IN_PROGRESS";
    return "READY";
  })();

  const extractionPct = (() => {
    const total = extractionStats?.total_count || 0;
    if (total > 0) return pctOf(extractionStats.completed_count, total);
    return (extractionStats?.model_count || 0) > 0 ? 10 : 0;
  })();

  const reportsCreated = genbiTemplates.filter((t) => t.is_created).length;
  const reportsActive = genbiTemplates.filter((t) => t.is_active).length;
  const reportsStatus = (() => {
    if (genbiTemplates.length === 0 || reportsActive === 0) return "LOCKED";
    if (reportsCreated === genbiTemplates.length) return "COMPLETED";
    if (reportsCreated > 0) return "IN_PROGRESS";
    return "READY";
  })();

  // ═══════════════════════════════════════════════════════════════
  // Per-step content
  // ═══════════════════════════════════════════════════════════════

  const palette = FILL;

  const absPending = Math.max(abs.total - abs.qc.included - abs.qc.excluded, 0);
  const ftPending = Math.max(ft.total - ft.qc.included - ft.qc.excluded, 0);

  const screeningFlow = (ph, firstLabel, withLink) => {
    const nodes = [{ value: fmt(ph.total), label: firstLabel }];
    if (withLink && ph.link) nodes.push({ value: fmt(ph.link.linked), label: "Full texts linked" });
    if (ph.hasAi) nodes.push({ value: fmt(ph.ai.reviewed), label: "AI reviewed" });
    if (ph.human.total > 0) nodes.push({ value: fmt(ph.human.reviewed), label: "Human reviewed" });
    if (ph.conflicts.applicable && ph.conflicts.total > 0)
      nodes.push({ value: fmt(ph.conflicts.resolved), label: "Conflicts resolved" });
    nodes.push({ value: ph.qc.done ? fmt(ph.qc.included) : "—", label: ph.qc.done ? "Included" : "Included (QC pending)" });
    return nodes;
  };

  const steps = [
    {
      key: "discovery",
      num: 1,
      title: "Literature discovery",
      short: "Discovery",
      big: abstractsCount,
      bigLabel: "abstracts",
      status: discoveryStatus,
      pct: discoveryStatus === "COMPLETED" ? 100 : discoveryStatus === "IN_PROGRESS" ? 50 : 0,
      summary:
        abstractsCount > 0
          ? `${fmt(abstractsCount)} abstracts from ${linkedSearches.length} linked search${linkedSearches.length === 1 ? "" : "es"}`
          : "Link a search or upload abstracts to begin",
      flow: [
        { value: fmt(migratedCount), label: "From discovery" },
        { value: fmt(manualCount), label: "Manual upload" },
        { value: fmt(abstractsCount), label: "Total abstracts" },
      ],
      flowJoin: "sum",
      searches: linkedSearches,
      actionLabel: discoveryStatus === "COMPLETED" ? "Open discovery" : "Continue",
      onClick: handleLiteratureDiscoveryClick,
    },
    {
      key: "abstract",
      num: 2,
      title: "Title and abstract screening",
      short: "Abstract screening",
      big: abs.total > 0 ? (abs.qc.done ? abs.qc.included : abs.ai.reviewed) : null,
      bigLabel: abs.qc.done ? "included" : "reviewed",
      status: absStatus,
      pct: phasePct(abs),
      phase: 1,
      summary: abs.qc.done
        ? "Closed after QC"
        : abs.total > 0
        ? `${fmt(abs.total)} abstracts to screen`
        : "Unlocks when abstracts are available",
      flow: abs.total > 0 ? screeningFlow(abs, "Abstracts", false) : null,
      split:
        abs.total > 0
          ? [
              { label: "Included", n: abs.qc.included, color: palette.included },
              { label: "Excluded", n: abs.qc.excluded, color: palette.excludedAbs },
              { label: "Not screened", n: absPending, color: palette.notScreened },
            ]
          : null,
      exportMode: "QC",
      actionLabel: absStatus === "COMPLETED" ? "Open screening" : "Continue",
      onClick: absStatus === "LOCKED" ? null : handleScreeningClick,
    },
    {
      key: "fulltext",
      num: 3,
      title: "Full-text screening",
      short: "Full-text screening",
      big: ft.total > 0 ? (ft.qc.done ? ft.qc.included : ft.link?.linked ?? 0) : null,
      bigLabel: ft.qc.done ? "included" : "linked",
      status: ftStatus,
      pct: phasePct(ft),
      phase: 2,
      summary: ft.qc.done
        ? "Closed after QC"
        : ft.total > 0
        ? `${fmt(ft.total)} studies carried from abstract screening`
        : "Unlocks when abstract screening includes studies",
      flow: ft.total > 0 ? screeningFlow(ft, "Carried in", true) : null,
      split:
        ft.total > 0
          ? [
              { label: "Included", n: ft.qc.included, color: palette.included },
              { label: "Excluded", n: ft.qc.excluded, color: palette.excludedFt },
              { label: "Not assessed", n: ftPending, color: palette.notAssessed },
            ]
          : null,
      exportMode: "QC_FT",
      actionLabel: ftStatus === "COMPLETED" ? "Open screening" : "Continue",
      onClick: ftStatus === "LOCKED" ? null : handleScreeningClick,
    },
    {
      key: "extraction",
      num: 4,
      title: "Data extraction",
      short: "Data extraction",
      big: extractionStatus === "LOCKED" ? null : extractionStats?.total_count ? extractionStats.completed_count : ft.qc.included,
      bigLabel: extractionStats?.total_count ? "models done" : "studies waiting",
      status: extractionStatus,
      pct: extractionPct,
      summary:
        extractionStatus === "LOCKED"
          ? "Unlocks when full-text QC includes at least 1 study"
          : extractionStats?.total_count
          ? `${extractionStats.completed_count} of ${extractionStats.total_count} models completed`
          : `${fmt(ft.qc.included)} included studies waiting for extraction`,
      // No header action button: sheets are opened from the Textractor templates list below
      actionLabel: null,
      onClick: extractionStatus === "LOCKED" ? null : handleDataExtractionClick,
    },
    {
      key: "rob",
      num: 5,
      title: "Risk of bias",
      short: "Risk of bias",
      big: null,
      status: "COMING_SOON",
      pct: 0,
      summary: "Study quality assessment for included studies",
      onClick: null,
    },
    {
      key: "nma",
      num: 6,
      title: "Network meta-analysis",
      short: "NMA",
      big: null,
      status: "COMING_SOON",
      pct: 0,
      summary: "Indirect comparisons across treatments",
      onClick: null,
    },
    {
      key: "reports",
      num: 7,
      title: "Reports and synthesis",
      short: "Reports",
      big: genbiTemplates.length ? reportsCreated : null,
      bigLabel: "reports created",
      status: reportsStatus,
      pct: pctOf(reportsCreated, genbiTemplates.length),
      summary:
        genbiTemplates.length === 0
          ? "GenBI report templates"
          : `${reportsCreated} of ${genbiTemplates.length} templates created`,
      templates: genbiTemplates,
      onClick: null,
    },
  ];

  // ═══ Review insights (all from data already loaded) ═══
  const p1 = prismaData?.phase1 || {};
  const p2 = prismaData?.phase2 || {};
  const identified = num(p1.records_identified) || abstractsCount;
  const finalIncluded = ft.qc.done ? ft.qc.included : null;

  // AI–human disagreement across both phases (only when humans screened too).
  const humanReviewed = abs.human.reviewed + ft.human.reviewed;
  const aiHumanConflicts = abs.ai.conflicts + ft.ai.conflicts;

  const openWork =
    (abs.conflicts.applicable ? abs.conflicts.unresolved : 0) +
    (ft.conflicts.applicable ? ft.conflicts.unresolved : 0) +
    (ft.link?.pending || 0);

  const latestSearch = [...linkedSearches].sort(
    (a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
  )[0];
  const searchDays = daysSince(latestSearch?.updated_at || latestSearch?.created_at);

  const glance = [
    {
      label: "Overall yield",
      value: finalIncluded !== null && identified ? `${((finalIncluded / identified) * 100).toFixed(1)}%` : "—",
      note: finalIncluded !== null ? `${fmt(finalIncluded)} of ${fmt(identified)} records included` : "Final after full-text QC",
    },
    {
      label: "Abstract pass rate",
      value: abs.qc.done && abs.total ? `${pctOf(abs.qc.included, abs.total)}%` : "—",
      note: abs.qc.done ? `${fmt(abs.qc.included)} of ${fmt(abs.total)} moved to full text` : "After abstract QC",
    },
    {
      label: "AI and human disagreement",
      value: humanReviewed > 0 ? `${((aiHumanConflicts / humanReviewed) * 100).toFixed(1)}%` : "AI only",
      note: humanReviewed > 0 ? `${fmt(aiHumanConflicts)} conflicts in ${fmt(humanReviewed)} human reviews` : "No human double-screening",
    },
    {
      label: "Search age",
      value: ageLabel(searchDays),
      note: searchDays === null ? "No linked search" : searchDays > 365 ? "Rerun before publishing" : "Last search update",
      pill: searchDays !== null ? searchAgeTone(searchDays) : null,
    },
    {
      label: "Open work",
      value: fmt(openWork),
      note: openWork ? "Unresolved conflicts and unlinked full texts" : "Nothing waiting",
    },
  ];

  const completedCount = steps.filter((s) => s.status === "COMPLETED").length;
  const activeStep = steps.find((s) => ["IN_PROGRESS", "READY", "NOT_STARTED"].includes(s.status));

  // Rail selection defaults to the current step; the user can browse any other.
  const selectedStep =
    steps.find((s) => s.key === selectedKey) ||
    activeStep ||
    [...steps].reverse().find((s) => s.status === "COMPLETED") ||
    steps[0];

  const selectStep = (key) => setSelectedKey(key);


  const framework = formatEnum(fabricDetails?.framework);
  const picoJson = fabricDetails?.pico_json;
  const hasPico =
    picoJson && (typeof picoJson === "string" ? picoJson.trim() !== "" : Object.keys(picoJson).length > 0);

  // ═══════════════════════════════════════════════════════════════
  // Renderers
  // ═══════════════════════════════════════════════════════════════

  const renderFlow = (step) => {
    if (!step.flow?.length) return null;
    const joinIcon = (i) =>
      step.flowJoin === "sum" ? (i === step.flow.length - 1 ? "bi-arrow-right" : "bi-plus") : "bi-chevron-right";
    return (
      <div>
        <p className="ev-small-p-dark ev-bold-med mb-1">Record flow</p>
        <div className="d-flex align-items-stretch gap-2">
          {step.flow.map((node, i) => (
            <React.Fragment key={node.label}>
              {i > 0 && <i className={`bi ${joinIcon(i)} ev-small-p-dark text-secondary align-self-center`} aria-hidden="true" />}
              <div className="ev-card-2 ev-radius-small p-3 flex-fill">
                <p className={`${i === step.flow.length - 1 ? "ev-large-p-blue" : "ev-large-p-dark"} mb-0 text-nowrap`}>
                  {node.value}
                </p>
                <p className="ev-xsmall-p-dark text-secondary mb-0">{node.label}</p>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderSplit = (step) => {
    if (!step.split) return null;
    const total = step.split.reduce((a, p) => a + p.n, 0);
    return (
      <div>
        <p className="ev-small-p-dark ev-bold-med mb-1">Decision split</p>
        <SplitBar parts={step.split} />
        <div className="d-flex flex-wrap gap-3 mt-1">
          {step.split.map((p) => (
            <span key={p.label} className="ev-small-p-dark d-inline-flex align-items-center gap-2">
              <ColorDot color={p.color} size={8} />
              {p.label} <span className="ev-bold-med">{fmt(p.n)}</span>
              <span className="text-secondary">{pctOf(p.n, total)}%</span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderTemplates = (step) => {
    if (!step.templates?.length) return null;
    return (
      <div>
        <p className="ev-small-p-dark ev-bold-med mb-2">Report templates</p>
        <div className="d-flex flex-column gap-2 scrollable-element vh-15 pe-1">
          {loadingGenbi && <p className="ev-xsmall-p-dark mb-0"></p>}
          {step.templates.map((t) => (
            <div key={t.id} className="ev-card-2 ev-radius-small px-3 py-2 d-flex justify-content-between align-items-center gap-2">
              <span className="ev-small-p-dark">{t.template_name}</span>
              {t.is_active ? (
                <button type="button" className="ev-btn-blue-outline-round ev-btn-small" onClick={() => executeOpenTemplate(t)}>
                  {t.is_created ? "Open" : "Create"}
                </button>
              ) : (
                <span className="ev-xsmall-p-dark text-secondary">
                  <i className="bi bi-lock me-1" aria-hidden="true" />
                  Activates after full-text QC
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderGlance = () => (
    <div className="ev-card-4 shadow-none mb-3">
      <p className="ev-small-p-dark ev-bold-med mb-2">Review at a glance</p>
      <div className="row row-cols-2 row-cols-md-3 row-cols-xl-5 g-2">
        {glance.map((g) => (
          <div key={g.label} className="col">
            <div className="ev-card-2 ev-radius-small p-3 h-100">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <span className="ev-xsmall-p-dark text-secondary">{g.label}</span>
                {g.pill && <span className={`${g.pill} ev-btn-xsmall`}>{searchDays > 365 ? "Old" : searchDays > 180 ? "Ageing" : "Fresh"}</span>}
              </div>
              <p className="ev-large-p-dark mb-0 text-nowrap">{g.value}</p>
              <p className="ev-xsmall-p-dark text-secondary mb-0">{g.note}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Top exclusion reasons for the selected screening phase (PRISMA).
  const renderExclusions = (step) => {
    if (!step.phase || !prismaData) return null;
    const reasons = (step.phase === 1 ? p1.exclusion_reasons : p2.exclusion_reasons) || [];
    if (!reasons.length) return null;
    const top = reasons.slice(0, 5);
    const max = Math.max(1, ...top.map((r) => num(r.count)));
    const color = step.phase === 1 ? palette.excludedAbs : palette.excludedFt;
    return (
      <div>
        <p className="ev-small-p-dark ev-bold-med mb-2">Top exclusion reasons</p>
        <div className="ev-card-2 ev-radius-small px-3 py-2">
          {top.map((r) => (
            <div key={r.reason} className="row g-2 align-items-center py-1">
              <div className="col-5 col-md-4">
                <span className="ev-small-p-dark text-truncate d-block" title={r.reason}>
                  {r.reason}
                </span>
              </div>
              <div className="col">
                <MiniBar pct={(num(r.count) / max) * 100} color={color} />
              </div>
              <div className="col-2 col-md-1 text-end">
                <span className="ev-small-p-dark ev-bold-med">{fmt(r.count)}</span>
              </div>
            </div>
          ))}
        </div>
        {step.phase === 2 && num(p2.studies_included_unique) > 0 && (
          <p className="ev-xsmall-p-dark text-secondary mb-0 mt-2">
            {fmt(p2.studies_included_unique)} unique studies from {fmt(p2.studies_included)} included reports
          </p>
        )}
      </div>
    );
  };

  const renderSearches = (step) => {
    if (!step.searches?.length) return null;
    return (
      <div>
        <p className="ev-small-p-dark ev-bold-med mb-2">Linked search</p>
        <div className="d-flex flex-column gap-2">
          {step.searches.map((s) => (
            <div key={s.id} className="ev-card-2 ev-radius-small px-3 py-2">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <p className="ev-small-p-dark ev-bold-med mb-1 text-truncate" title={s.research_question || s.search_query}>
                  {s.research_question || s.search_query || "Linked search"}
                </p>
                <span className={`${searchAgeTone(daysSince(s.updated_at || s.created_at))} ev-btn-xsmall flex-shrink-0`}>
                  {ageLabel(daysSince(s.updated_at || s.created_at))}
                </span>
              </div>
              {s.research_question && s.search_query && (
                <p className="ev-xsmall-p-dark text-secondary mb-1 text-truncate" title={s.search_query}>
                  {s.search_query}
                </p>
              )}
              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                <span className="discovery-badge-blue ev-btn-xsmall">{s.source_type || "PUBMED"}</span>
                <span className="ev-xsmall-p-dark">{fmt(s.number_of_citations)} records found</span>
                {Array.isArray(s.additional_search_strategies) && s.additional_search_strategies.length > 0 && (
                  <span className="ev-xsmall-p-dark text-secondary">
                    +{s.additional_search_strategies.length} additional strategies
                  </span>
                )}
                {Array.isArray(s.discovery_access) && s.discovery_access.length > 0 && (
                  <span className="d-inline-flex align-items-center gap-1 ms-auto" title={s.discovery_access.map((u) => u.name || u.email).join(", ")}>
                    {s.discovery_access.slice(0, 4).map((u) => (
                      <span key={u.user_id || u.email} className="nx-phase-dot discovery-badge-blue rounded-circle d-inline-flex align-items-center justify-content-center p-0">
                        {initials(u.name || u.email)}
                      </span>
                    ))}
                    {s.discovery_access.length > 4 && (
                      <span className="ev-xsmall-p-dark text-secondary">+{s.discovery_access.length - 4}</span>
                    )}
                  </span>
                )}
              </div>
              <p className="ev-xsmall-p-dark text-secondary mb-0">
                {[
                  s.source_type,
                  s.owner && `owner ${s.owner}`,
                  s.updated_at && `updated ${new Date(s.updated_at).toLocaleDateString()}`,
                  s.abstract_migration_status && `abstracts ${String(s.abstract_migration_status).toLowerCase()}`,
                  s.fulltext_migration_status && `full texts ${String(s.fulltext_migration_status).toLowerCase()}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTextractorTemplates = (step) => {
    if (step.key !== "extraction" || !textractorTemplates.length) return null;
    return (
      <div>
        <p className="ev-small-p-dark ev-bold-med mb-2">Textractor templates</p>
        {/* Scrollable once the list is taller than the box */}
        <div className="d-flex flex-column gap-2 scrollable-element vh-15 pe-1">
          {loadingTextractor && <p className="ev-xsmall-p-dark mb-0"></p>}
          {textractorTemplates.map((t) => (
            <div
              key={t.id}
              className="ev-card-2 ev-radius-small px-3 py-2 d-flex justify-content-between align-items-center gap-2"
            >
              <span className="ev-small-p-dark">{textractorTemplateLabel(t)}</span>
              {t.is_active ? (
                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-btn-small"
                  onClick={() => executeOpenTextractorTemplate(t)}
                >
                  {t.is_created ? "Open" : "Create"}
                </button>
              ) : (
                <span className="ev-xsmall-p-dark text-secondary">
                  <i className="bi bi-lock me-1" aria-hidden="true" />
                  Activates after full-text QC
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderComingSoon = (step) =>
    step.status === "COMING_SOON" ? (
      <div className="ev-card-2 ev-radius-small p-4 text-center">
        <i className="bi bi-diagram-3 ev-med-p-blue" aria-hidden="true" />
        <p className="ev-small-p-dark ev-bold-med mb-1 mt-2">{step.title} is on the way</p>
        <p className="ev-xsmall-p-dark text-secondary mb-0">
          It will use the {fmt(ft.qc.included)} studies included after full-text QC.
        </p>
      </div>
    ) : null;

  const renderPico = () => {
    if (!hasPico) return null;
    if (typeof picoJson === "string") return <p className="ev-small-p-dark mb-0">{picoJson}</p>;
    return Object.entries(picoJson).map(([k, v]) => (
      <div key={k} className="mb-2">
        <p className="ev-xsmall-p-dark ev-bold-med mb-0">{k}</p>
        <p className="ev-small-p-dark mb-0">
          {Array.isArray(v) ? v.join(", ") : typeof v === "object" && v !== null ? JSON.stringify(v) : String(v)}
        </p>
      </div>
    ));
  };

  const sel = selectedStep;
  const selMeta = STATUS_META[sel.status];

  // ═══════════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════════

  return (
    <>
      {/* ================= HEADER ================= */}
      <div className="ev-card-2 dashboard-header position-static mb-1 w-100">
        <div className="d-flex align-items-center gap-2">
          <button className="ev-btn-yellow-fill-round ev-btn-reg ev-bold-reg" onClick={onBackToProjects}>
            <i className="bi bi-arrow-left me-1"></i>
            Back to Projects
          </button>
        </div>
        <p className="gradient-font5 ev-fs-18 ev-bold-med mb-0">Screening Space</p>
        <div className="d-flex gap-2 align-items-center">
          <button className="ev-btn-yellow-fill-round ev-btn-reg ev-bold-reg" onClick={onCreateProject}>
            <i className="bi bi-plus-circle me-1"></i>
            New SLR Project
          </button>
        </div>
      </div>

      <div className="scrollable-element vh-80 pe-2">
        {/* ================= PROJECT ROW ================= */}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2 mt-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="ev-large-p-dark ev-bold-med">{projectName}</span>
            {hasPico && (
              <button type="button" className="ev-btn-text-only-fill ev-btn-small p-0" onClick={() => setShowPico(true)}>
                <i className="bi bi-info-circle me-1" aria-hidden="true" />
                View criteria
              </button>
            )}
          </div>
          <div className="d-flex gap-2 align-items-center">
            <button
              className="ev-btn-lightblue-fill-round ev-btn-reg ev-bold-reg"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <i className="bi bi-arrow-repeat me-1"></i> Refresh
            </button>
            <button className="ev-btn-lightblue-fill-round ev-btn-reg ev-bold-reg" onClick={() => onEditProject()}>
              <i className="bi bi-gear me-1"></i> Project Settings
            </button>
          </div>
        </div>

        {/* ================= CHEVRON PIPELINE ================= */}
        <div className="ev-card-4 shadow-none mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="ev-small-p-dark ev-bold-med">Pipeline</span>
            <span className="ev-xsmall-p-dark text-secondary">
              {completedCount} of {steps.length} steps done
            </span>
          </div>
          <div className="overflow-auto">
            <div className="slr-chevron-row" role="tablist" aria-label="SLR steps">
              {steps.map((s) => {
                const isSel = s.key === sel.key;
                const isDim = ["LOCKED", "COMING_SOON"].includes(s.status);
                return (
                  <button
                    key={s.key}
                    type="button"
                    role="tab"
                    aria-selected={isSel}
                    className={`slr-chevron slr-chevron--${CHEVRON_TONE[s.status]} ${isSel ? "selected" : ""} ${
                      isDim && !isSel ? "is-muted" : ""
                    }`}
                    onClick={() => selectStep(s.key)}
                  >
                    <span className="d-flex align-items-center gap-2">
                      <StepBadge status={s.status} num={s.num} />
                      <span className="ev-xsmall-p-dark text-secondary">Step {s.num}</span>
                    </span>
                    <span className="ev-small-p-dark ev-bold-med text-nowrap">{s.short}</span>
                    <span className="ev-med-p-dark mb-0 text-nowrap">{s.big !== null ? fmt(s.big) : "—"}</span>
                    <span className="ev-xsmall-p-dark text-secondary text-nowrap">
                      {s.big !== null ? s.bigLabel : STATUS_META[s.status].label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {renderGlance()}

        {/* ================= SELECTED STEP ================= */}
        <div className="ev-card-4 shadow-none mb-1">
          <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 pb-3 border-bottom">
            <div className="d-flex align-items-center gap-3">
              <StepBadge status={sel.status} num={sel.num} />
              <div>
                <span className="ev-xsmall-p-dark text-secondary d-block">
                  Step {sel.num} of {steps.length}
                </span>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="ev-med-p-dark">{sel.title}</span>
                  <span className={selMeta.pill}>{sel.status === "IN_PROGRESS" ? `${sel.pct}%` : selMeta.label}</span>
                </div>
                <span className="ev-small-p-dark text-secondary d-block">{sel.summary}</span>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              {sel.exportMode && sel.status === "COMPLETED" && (
                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-btn-reg"
                  disabled={isDownloading}
                  onClick={() => handleDownload(sel.exportMode)}
                >
                  <i className="bi bi-download me-1" aria-hidden="true" />
                  {isDownloading ? "Downloading..." : "QC export"}
                </button>
              )}
              {sel.onClick && sel.actionLabel && (
                <button type="button" className="ev-btn-dark-blue-fill-round ev-btn-reg ev-bold-reg" onClick={sel.onClick}>
                  {sel.actionLabel}
                  <i className="bi bi-arrow-right ms-1" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          <div className="d-flex flex-column gap-3 mt-1">
            {renderFlow(sel)}
            {renderSplit(sel)}
            {renderExclusions(sel)}
            {renderSearches(sel)}
            {renderTemplates(sel)}
            {renderTextractorTemplates(sel)}
            {renderComingSoon(sel)}
          </div>
        </div>
      </div>

      {/* ================= PICO modal ================= */}
      {showPico && (
        <div className="popup" onClick={() => setShowPico(false)}>
          <div className="popup-content slr-genbi-picker" onClick={(e) => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <p className="ev-med-p-blue ev-bold-med mb-0">{framework || "Eligibility"} criteria</p>
              <button className="ev-btn-light-fill ev-btn-small" onClick={() => setShowPico(false)} aria-label="Close">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            {renderPico()}
          </div>
        </div>
      )}

      {/* ================= Popups ================= */}
      {isProcessingPopupVisible && <ProcessingPopup message={processingMessage || "Processing..."} />}
      {isErrorPopupVisible && <ErrorPopup message={popupErrorMessage} onClose={closeErrorPopup} />}
      {isSuccessPopupVisible && <SuccessPopup message={popupSuccessMessage} onClose={closeSuccessPopup} />}
    </>
  );
};

export default ScreeningSpaceProjectOverview;